<script setup lang="ts">
import { BlinkyAvatar } from '~ds/components/ui/blinky-avatar';
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Heading } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

/**
 * The sign-up card closing a public page for visitors who can still sign up. Blinky leads it when
 * `isBlinky`, otherwise the Anyway mark does; `disabled` is for a CMS preview, which has nowhere to
 * go, so the button stays disabled rather than a dead link.
 */
interface Props {
  to: string;
  title: string;
  buttonLabel: string;
  isBlinky?: boolean;
  disabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  isBlinky: false,
  disabled: false,
});

const emit = defineEmits<{
  click: [];
}>();
</script>

<template>
  <section
    data-testid="sign-up-cta-card"
    class="flex w-full max-w-3xl flex-col gap-6 rounded-2xl bg-primary p-5 text-primary-foreground sm:flex-row sm:items-center sm:justify-between sm:p-6"
  >
    <div class="flex items-start gap-4 sm:items-center">
      <BlinkyAvatar
        v-if="isBlinky"
        size="lg"
        class="shrink-0"
      />
      <NuxtImg
        v-else
        src="/images/kui/anyway/anyway-a-mark.svg"
        alt=""
        width="44"
        height="44"
        loading="lazy"
        class="size-11 shrink-0"
      />
      <Heading size="md" as="h2">
        {{ title }}
      </Heading>
    </div>
    <Button
      v-if="disabled"
      variant="secondary"
      size="xl"
      disabled
      type="button"
      class="w-full sm:w-auto"
    >
      {{ buttonLabel }}
      <Icon :name="ICONS.arrowRight2" />
    </Button>
    <Button
      v-else
      variant="secondary"
      size="xl"
      class="w-full sm:w-auto"
      as-child
      @click="emit('click')"
    >
      <NuxtLink :to="to">
        {{ buttonLabel }}
        <Icon :name="ICONS.arrowRight2" />
      </NuxtLink>
    </Button>
  </section>
</template>
